export type InkShades = {
  ink0: string;
  ink1: string;
  ink2: string;
  ink3: string;
  ink4: string;
};

export const DEFAULT_INK: InkShades = {
  ink0: "#03060F",
  ink1: "#060B1E",
  ink2: "#0A143A",
  ink3: "#122360",
  ink4: "#1E3AA8",
};

const INK_CSS_KEYS = ["--ink-0", "--ink-1", "--ink-2", "--ink-3", "--ink-4"] as const;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360;
  if (s === 0) {
    const v = Math.round(l * 255);
    return `#${v.toString(16).padStart(2, "0").repeat(3)}`;
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  const pad = (n: number) => n.toString(16).padStart(2, "0");
  return `#${pad(r)}${pad(g)}${pad(b)}`;
}

function blendHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * t);
  const pad = (n: number) => n.toString(16).padStart(2, "0");
  return `#${pad(mix(ar, br))}${pad(mix(ag, bg))}${pad(mix(ab, bb))}`;
}

/**
 * Derive dark site background shades tinted by event accent colors.
 * Keeps luminance very low so typography stays readable.
 */
export function accentToInkShades(primary: string, deep: string): InkShades {
  const blend = blendHex(primary, deep, 0.4);
  const [r, g, b] = hexToRgb(blend);
  const [h, s] = rgbToHsl(r, g, b);
  const sat = Math.min(Math.max(s * 0.5, 0.12), 0.38);
  return {
    ink0: hslToHex(h, sat * 0.75, 0.028),
    ink1: hslToHex(h, sat, 0.058),
    ink2: hslToHex(h, sat, 0.1),
    ink3: hslToHex(h, sat * 0.92, 0.165),
    ink4: hslToHex(h, sat * 0.88, 0.27),
  };
}

/**
 * Derive dark site background shades from a poster edge color.
 * Preserves the border hue while keeping luminance in a readable night-sky range.
 */
export function edgeToInkShades(edgeColor: string): InkShades {
  const [r, g, b] = hexToRgb(edgeColor);
  const [h, s] = rgbToHsl(r, g, b);
  const sat = Math.min(Math.max(s * 0.65, 0.1), 0.45);
  return {
    ink0: hslToHex(h, sat * 0.72, 0.028),
    ink1: hslToHex(h, sat, 0.058),
    ink2: hslToHex(h, sat, 0.102),
    ink3: hslToHex(h, sat * 0.94, 0.168),
    ink4: hslToHex(h, sat * 0.9, 0.275),
  };
}

export function applyInkShades(shades: InkShades): void {
  const root = document.documentElement.style;
  root.setProperty("--ink-0", shades.ink0);
  root.setProperty("--ink-1", shades.ink1);
  root.setProperty("--ink-2", shades.ink2);
  root.setProperty("--ink-3", shades.ink3);
  root.setProperty("--ink-4", shades.ink4);
}

export function resetInkShades(): void {
  const root = document.documentElement.style;
  for (const key of INK_CSS_KEYS) root.removeProperty(key);
}

/** For admin preview panels that cannot rely on :root overrides. */
export function inkShadesToCssVars(shades: InkShades): Record<string, string> {
  return {
    "--ink-0": shades.ink0,
    "--ink-1": shades.ink1,
    "--ink-2": shades.ink2,
    "--ink-3": shades.ink3,
    "--ink-4": shades.ink4,
  };
}
